import {fromJson} from '@github-ui/dotcom-schema/protobuf'
import {RepoIssuesDashboardContentResponseSchema} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {queryFnFetchWithProtoSignal} from '@github-ui/react-core/future/query-fn-fetch'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'

import {mergeDeferredIssues} from '../helpers/merge-deferred-issues'
import {repoDeferredToListItem} from '../helpers/repo-issues-deferred'
import {createRowNodeLoader} from './issues-dashboard-row-node'
import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'

export type RepoRowItem = ReturnType<typeof repoIssuesToListItems>[number]

type RowsEnvelope = {
  payload?: {
    issuesIndexRowsData?: unknown
  }
}

export function shouldFetchReactions(query: string): boolean {
  return /\bsort:reactions(?:-\S+)?\b/.test(query)
}

/**
 * Fetch fresh enriched rows for the repository Issues index from
 * `GET /:owner/:repo/issues/_rows`, keyed by each row's global identity so the
 * batching loader can resolve individual requests.
 */
export async function fetchRepoRowNodes(
  owner: string,
  repo: string,
  globalIds: string[],
  fetchReactions = false,
  milestoneNumber?: number,
): Promise<Map<string, RepoRowItem>> {
  const {json} = await queryFnFetchWithProtoSignal<RowsEnvelope>({
    routeId: 'issuesIndexRowsData',
    queryName: 'rows',
    queryDeps: {
      pathname: `/${owner}/${repo}/issues/_rows`,
      searchParams: {
        ids: globalIds.join(','),
        fetch_reactions: fetchReactions.toString(),
        milestone_number: milestoneNumber?.toString() ?? null,
      },
    },
  })
  const rawRows = json.payload?.issuesIndexRowsData
  if (!rawRows) return new Map()

  const rows = fromJson({
    schema: RepoIssuesDashboardContentResponseSchema,
    raw: rawRows,
    routeId: 'issuesIndexRowsData',
    queryName: 'rows',
  })
  const deferred = rows.deferred.map(repoDeferredToListItem).filter(item => item !== undefined)
  const results = mergeDeferredIssues(repoIssuesToListItems(rows.results), deferred)

  const byIdentity = new Map<string, RepoRowItem>()
  for (const item of results) {
    byIdentity.set(getListItemIdentity(item), item)
  }
  return byIdentity
}

// The repository Issues index row-node loader, over GET /:owner/:repo/issues/_rows.
// Built per page (owner/repo are stable there) so it reuses the shared batching
// queue without sharing pending state with the dashboard loader.
export function createRepoRowNodeLoader(
  owner: string,
  repo: string,
  fetchReactions = false,
  milestoneNumber?: number,
): {
  loadRowNode: (globalId: string) => Promise<RepoRowItem | undefined>
} {
  return createRowNodeLoader<RepoRowItem>(globalIds =>
    fetchRepoRowNodes(owner, repo, globalIds, fetchReactions, milestoneNumber),
  )
}
